import { Badge, Button, IconButton } from '@artosera/ui';
import { formatDate } from '@artosera/shared';
import { ChevronLeft, ChevronRight, Download, Lock, Plus, TriangleAlert } from 'lucide-react';
import type { JSX } from 'react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router';
import {
  useDocuments,
  useEcheancesDocuments,
  useExporterJournalCoffre,
  type FiltresDocuments,
} from '../../api/coffre.js';
import { useTableauDeBord } from '../../api/tableauDeBord.js';
import { estApiError } from '../../api/errors.js';
import { useEquipe } from '../../api/equipe.js';
import { galerieALaction } from '../../auth/sujet.js';
import { useShellContext } from '../shell/context.js';
import { teinteDeLaConfidentialite } from './confidentialiteUi.js';
import { EcheancesPanel } from './EcheancesPanel.js';
import { FeuilleDocument } from './FeuilleDocument.js';
import { NouveauDocumentDialog } from './NouveauDocumentDialog.js';
import { RattachementPastille } from './RattachementPastille.js';
import {
  aUneRestriction,
  BarreCoffre,
  ETAT_BARRE_VIDE,
  type EtatBarreCoffre,
} from './BarreCoffre.js';
import { joursRestantsEcheance, teinteDeLEcheance } from './echeanceUi.js';
import styles from './CoffrePage.module.css';
import { Verrouillable } from '../module/LectureSeule.js';

type Vue = 'documents' | 'echeances';

function versLaDateIso(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function telechargerBlob(blob: Blob, nomFichier: string): void {
  const url = URL.createObjectURL(blob);
  const lien = window.document.createElement('a');
  lien.href = url;
  lien.download = nomFichier;
  window.document.body.appendChild(lien);
  lien.click();
  lien.remove();
  URL.revokeObjectURL(url);
}

/**
 * Le coffre (3.3, écran 4 ; § 18) : arborescence par type à gauche, liste
 * filtrable au centre, tableau de bord des échéances dans la même
 * arborescence. La fiche (`DocumentDetailPage`) vit sur sa propre route,
 * `/coffre/:id`.
 */
export function CoffrePage(): JSX.Element {
  const { t, i18n } = useTranslation(['regie', 'coffre', 'commun']);
  const langue = i18n.language === 'en' ? 'en' : 'fr';
  const navigate = useNavigate();
  const { galerieSlug, galerie } = useShellContext();
  const [searchParams, setSearchParams] = useSearchParams();

  const vue: Vue = searchParams.get('vue') === 'echeances' ? 'echeances' : 'documents';
  function setVue(valeur: Vue): void {
    const suivants = new URLSearchParams(searchParams);
    if (valeur === 'echeances') suivants.set('vue', valeur);
    else suivants.delete('vue');
    setSearchParams(suivants);
  }
  const [etat, setEtat] = useState<EtatBarreCoffre>(ETAT_BARRE_VIDE);
  const [page, setPage] = useState(1);
  const { q, type: typeFiltre, rattachement: rattachementFiltre, tri } = etat;
  const { confidentialite, etiquette, porteeEcheance, inclureArchives } = etat;
  const restreint = aUneRestriction(etat);

  // Toute modification de la barre ramène à la première page.
  function modifierBarre(partiel: Partial<EtatBarreCoffre>): void {
    setEtat((courant) => ({ ...courant, ...partiel }));
    setPage(1);
  }

  // Comptages de la barre : le tableau de bord les rend agrégés (§ 20).
  // La page en tirait auparavant une liste de deux cents documents qu'elle
  // comptait elle-même — donc faux au deux cent unième, et tout le coffre sur
  // le réseau pour afficher dix-neuf nombres.
  //
  // Les compteurs portent les documents VIVANTS : les archives se voient dans
  // la liste, en activant « archivés », et pas dans ces comptes.
  const tableau = useTableauDeBord(galerieSlug);
  const comptesParType = tableau.data?.coffre?.parType ?? {};
  const totalDocuments = tableau.data?.coffre?.total ?? 0;

  const echeances = useEcheancesDocuments(galerieSlug);
  const nbEnRetard = echeances.data?.echeances.enRetard.length ?? 0;
  const nbAVenir = echeances.data?.echeances.aVenir.length ?? 0;

  const filtres: FiltresDocuments = useMemo(() => {
    const bornesEcheance: Pick<FiltresDocuments, 'echeanceAvant'> = {};
    if (porteeEcheance === 'enRetard') {
      Object.assign(bornesEcheance, { echeanceAvant: versLaDateIso(new Date()) });
    } else if (porteeEcheance === 'sousTrente') {
      const dansTrenteJours = new Date();
      dansTrenteJours.setDate(dansTrenteJours.getDate() + 30);
      Object.assign(bornesEcheance, { echeanceAvant: versLaDateIso(dansTrenteJours) });
    }
    return {
      ...(typeFiltre ? { type: [typeFiltre] } : {}),
      ...(rattachementFiltre
        ? { rattache: `${rattachementFiltre.type}:${rattachementFiltre.id}` }
        : {}),
      ...(confidentialite ? { confidentialite } : {}),
      ...(etiquette ? { etiquette } : {}),
      ...(q ? { q } : {}),
      ...bornesEcheance,
      inclureArchives,
      tri,
      page,
      parPage: 50,
    };
  }, [
    typeFiltre,
    rattachementFiltre,
    confidentialite,
    etiquette,
    q,
    porteeEcheance,
    inclureArchives,
    tri,
    page,
  ]);

  const liste = useDocuments(galerieSlug, filtres);
  const documents = liste.data?.documents ?? [];

  const equipe = useEquipe(galerieSlug);
  const nomsParCompte = useMemo(() => {
    const table = new Map<string, string>();
    for (const membre of equipe.data?.membres ?? []) table.set(membre.compteId, membre.nom);
    return table;
  }, [equipe.data]);

  const exporterJournal = useExporterJournalCoffre(galerieSlug);
  const [erreurExport, setErreurExport] = useState<string | null>(null);

  const peutDeposer = galerieALaction(galerie, 'documents:write');
  const peutVoirJournal = galerieALaction(galerie, 'document-access:read');

  const nouveauOuvert = searchParams.get('nouveau') === '1';
  function fermerNouveau(): void {
    const suivants = new URLSearchParams(searchParams);
    suivants.delete('nouveau');
    setSearchParams(suivants, { replace: true });
  }
  function ouvrirNouveau(): void {
    const suivants = new URLSearchParams(searchParams);
    suivants.set('nouveau', '1');
    setSearchParams(suivants);
  }

  function toutEffacer(): void {
    setEtat((courant) => ({ ...ETAT_BARRE_VIDE, tri: courant.tri }));
    setPage(1);
  }

  async function exporter(): Promise<void> {
    setErreurExport(null);
    try {
      const { blob, nomFichier } = await exporterJournal.mutateAsync();
      telechargerBlob(blob, nomFichier ?? `coffre-journal-${galerieSlug}.csv`);
    } catch (error) {
      setErreurExport(estApiError(error) ? error.message : t('commun:erreurReseau'));
    }
  }

  return (
    <div className={styles.enveloppe}>
      <div className={styles.entete}>
        <h1 className={styles.titre}>{t('coffre:titre')}</h1>
        <div className={styles.actionsEntete}>
          {peutVoirJournal ? (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => void exporter()}
              loading={exporterJournal.isPending}
            >
              <Download size={16} strokeWidth={1.5} aria-hidden />
              {t('regie:coffre.liste.exporterJournal')}
            </Button>
          ) : null}
          <Verrouillable action="documents:write" ouvert={peutDeposer}>
            <Button size="sm" onClick={ouvrirNouveau}>
              <Plus size={16} strokeWidth={1.5} aria-hidden />
              {t('regie:coffre.nouveauDocument.bouton')}
            </Button>
          </Verrouillable>
        </div>
      </div>

      {erreurExport ? (
        <p className={styles.erreur} role="alert">
          {erreurExport}
        </p>
      ) : null}

      <nav className={styles.vues} aria-label={t('regie:coffre.vues.titre')}>
        <button
          type="button"
          className={styles.vue}
          aria-current={vue === 'documents' ? 'page' : undefined}
          onClick={() => setVue('documents')}
        >
          {t('regie:coffre.vues.documents')}
          <span className={styles.compte}>{totalDocuments}</span>
        </button>
        <button
          type="button"
          className={styles.vue}
          aria-current={vue === 'echeances' ? 'page' : undefined}
          onClick={() => setVue('echeances')}
        >
          {t('regie:coffre.vues.echeances')}
          <span className={nbEnRetard > 0 ? styles.compteUrgent : styles.compte}>
            {nbEnRetard + nbAVenir}
          </span>
        </button>
      </nav>

      <div className={styles.contenu}>
        {vue === 'echeances' ? (
          <EcheancesPanel
            galerieSlug={galerieSlug}
            aVenir={echeances.data?.echeances.aVenir ?? []}
            enRetard={echeances.data?.echeances.enRetard ?? []}
            chargement={echeances.isLoading}
          />
        ) : (
          <div className={styles.page}>
            {nbEnRetard + nbAVenir > 0 ? (
              <button
                type="button"
                className={styles.bandeau}
                data-urgent={nbEnRetard > 0}
                onClick={() => setVue('echeances')}
              >
                <TriangleAlert size={16} strokeWidth={1.5} aria-hidden />
                <span className={styles.bandeauTexte}>
                  {[
                    nbEnRetard > 0
                      ? t('regie:coffre.bandeau.enRetard', { count: nbEnRetard })
                      : null,
                    nbAVenir > 0 ? t('regie:coffre.bandeau.aVenir', { count: nbAVenir }) : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
                <span className={styles.bandeauAction}>{t('regie:coffre.bandeau.voir')}</span>
              </button>
            ) : null}

            <BarreCoffre
              galerieSlug={galerieSlug}
              etat={etat}
              onChanger={modifierBarre}
              onToutEffacer={toutEffacer}
              comptesParType={comptesParType}
            />

            {liste.data && liste.data.confidentielsMasques > 0 ? (
              <p className={styles.confidentielsMasques}>
                {t('regie:coffre.liste.confidentielsMasques', {
                  count: liste.data.confidentielsMasques,
                })}
              </p>
            ) : null}

            {liste.isLoading ? (
              <p className={styles.vide}>{t('commun:chargement')}</p>
            ) : liste.isError ? (
              <p className={styles.vide} role="alert">
                {estApiError(liste.error) ? liste.error.message : t('commun:erreurReseau')}
              </p>
            ) : documents.length === 0 ? (
              <div className={styles.videFeuille}>
                <FeuilleDocument />
                <p>
                  {restreint
                    ? t('regie:coffre.liste.aucunResultatFiltre')
                    : t('regie:coffre.liste.aucunResultat')}
                </p>
                {restreint ? (
                  <Button variant="secondary" size="sm" onClick={toutEffacer}>
                    {t('regie:coffre.liste.filtres.toutEffacer')}
                  </Button>
                ) : peutDeposer ? (
                  <Button size="sm" onClick={ouvrirNouveau}>
                    <Plus size={16} strokeWidth={1.5} aria-hidden />
                    {t('regie:coffre.nouveauDocument.bouton')}
                  </Button>
                ) : null}
              </div>
            ) : (
              <div className={styles.tableauEnveloppe}>
                <table className={styles.tableau} data-density="registre">
                  <thead>
                    <tr>
                      <th scope="col">{t('regie:coffre.liste.colonneTitre')}</th>
                      <th scope="col">{t('regie:coffre.liste.colonneRattachements')}</th>
                      <th scope="col">{t('regie:coffre.liste.colonneConfidentialite')}</th>
                      <th scope="col">{t('regie:coffre.liste.colonneEcheance')}</th>
                      <th scope="col" className={styles.colonneVersions}>
                        {t('regie:coffre.liste.colonneVersions')}
                      </th>
                      <th scope="col">{t('regie:coffre.liste.colonneDepot')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {documents.map((document) => {
                      const joursRestants =
                        document.echeanceLe !== null
                          ? joursRestantsEcheance(document.echeanceLe)
                          : null;
                      return (
                        <tr key={document.id}>
                          <td className={styles.celTitre}>
                            <span className={styles.titreDocument}>
                              <FeuilleDocument
                                nomFichier={document.fichier.nom}
                                confidentiel={document.confidentialite === 'confidentiel'}
                                archive={document.archive}
                              />
                              <span className={styles.titreTexte}>
                                <Link
                                  className={styles.lienLigne}
                                  to={`/${galerieSlug}/coffre/${document.id}`}
                                >
                                  {document.titre}
                                </Link>
                                <span className={styles.typeDocument}>
                                  {t(`coffre:types.${document.type}`)}
                                  {document.archive ? (
                                    <Badge tone="withdrawn">
                                      {t('regie:coffre.liste.archive')}
                                    </Badge>
                                  ) : null}
                                </span>
                              </span>
                            </span>
                          </td>
                          <td className={styles.celRattachements}>
                            <div className={styles.pastilles}>
                              {document.rattachements.length === 0 ? (
                                <span className={styles.aucunRattachement}>—</span>
                              ) : (
                                document.rattachements.map((rattachement) => (
                                  <RattachementPastille
                                    key={`${rattachement.type}:${rattachement.id}`}
                                    galerieSlug={galerieSlug}
                                    rattachement={rattachement}
                                  />
                                ))
                              )}
                            </div>
                          </td>
                          <td className={styles.celConfidentialite}>
                            <Badge tone={teinteDeLaConfidentialite(document.confidentialite)}>
                              {document.confidentialite === 'confidentiel' ? (
                                <Lock size={11} aria-hidden />
                              ) : null}
                              {t(`coffre:confidentialite.${document.confidentialite}`)}
                            </Badge>
                          </td>
                          <td
                            className={styles.celEcheance}
                            data-libelle={t('regie:coffre.liste.colonneEcheance')}
                          >
                            {document.echeanceLe === null || joursRestants === null ? (
                              '—'
                            ) : (
                              <Badge tone={teinteDeLEcheance(joursRestants)}>
                                {formatDate(new Date(document.echeanceLe), langue, 'court')}
                              </Badge>
                            )}
                          </td>
                          <td
                            className={styles.colonneVersions}
                            data-libelle={t('regie:coffre.liste.colonneVersions')}
                          >
                            {document.versions.length + 1}
                          </td>
                          <td className={styles.colonneDepot}>
                            <span>
                              {formatDate(new Date(document.fichier.depose), langue, 'court')}
                            </span>
                            <span className={styles.deposePar}>
                              {document.fichier.deposePar
                                ? (nomsParCompte.get(document.fichier.deposePar) ??
                                  document.fichier.deposePar)
                                : '—'}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {liste.data && liste.data.pagination.pages > 1 ? (
              <div className={styles.pagination}>
                <IconButton
                  variant="outline"
                  icon={<ChevronLeft aria-hidden />}
                  aria-label={t('regie:coffre.liste.pagination.precedent')}
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage((valeur) => Math.max(1, valeur - 1))}
                />
                <span>
                  {t('regie:coffre.liste.pagination.page', {
                    page: liste.data.pagination.page,
                    pages: liste.data.pagination.pages,
                  })}
                </span>
                <IconButton
                  variant="outline"
                  icon={<ChevronRight aria-hidden />}
                  aria-label={t('regie:coffre.liste.pagination.suivant')}
                  size="sm"
                  disabled={page >= liste.data.pagination.pages}
                  onClick={() =>
                    setPage((valeur) => Math.min(liste.data!.pagination.pages, valeur + 1))
                  }
                />
              </div>
            ) : null}
          </div>
        )}
      </div>

      <NouveauDocumentDialog
        key={nouveauOuvert ? 'nouveau-ouvert' : 'nouveau-ferme'}
        galerieSlug={galerieSlug}
        open={nouveauOuvert}
        onOpenChange={(ouvert) => {
          if (!ouvert) fermerNouveau();
        }}
        onDepose={(id) => {
          fermerNouveau();
          navigate(`/${galerieSlug}/coffre/${id}`);
        }}
      />
    </div>
  );
}
